import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { GetCashRequestQuery, LoanClient } from '../../GraphQL.Loan';
import { Routes } from '../../Routes';
import { Captcha } from '../../shared/Captcha';
import { CustomerServicePhoneLink } from '../../shared/CustomerServicePhoneLink';
import { Link } from '../../shared/Link';
import { getModelState, ModelState } from '../../shared/ModelState';
import { parseNumber } from '../../shared/NumberHelper';
import { Spinner } from '../../shared/Spinner';
import { formatCurrency } from '../../shared/StringHelper';
import { Textbox, TextboxChangeEvent } from '../../shared/Textbox';
import { useEffectAsync } from '../../shared/useEffectAsync';
import { useGraphQL } from '../../shared/useGraphQL';
import { useNavigation } from '../../shared/useNavigation';
import { ValidationMessage } from '../../shared/ValidationMessage';
import { RefinanceDisclosures } from '../refinance/RefinanceDisclosures';
import { CurrencyTextbox } from '../../shared/CurrencyTextbox';

export default function GetCashSection() {
    const client = useGraphQL(LoanClient);
    const [model, setModel] = useState<GetCashRequestQuery['getCashRequest']>();
    const params = useParams<{ key: string }>();
    const key = parseNumber(params.key, 0);
    const [modelState, setModelState] = useState<ModelState>({});
    const [showError, setShowError] = useState(false);
    const [busy, setBusy] = useState(true);

    const [selectedAmt, setSelectedAmt] = useState('');
    const nav = useNavigation();

    useEffectAsync(
        async (signal) => {
            setBusy(true);
            const result = await client.getCashRequest(key, signal);
            setBusy(false);
            if (result.data?.getCashRequest?.redirect) {
                nav.redirect(result.data.getCashRequest.redirect);
            } else if (result.errors) {
                nav.redirect(Routes.ContactSupport.Root);
            } else {
                setModel(result.data?.getCashRequest);
            }
        },
        [client, key, nav]
    );

    const handleSelectedAmtChange = useCallback((e: TextboxChangeEvent) => {
        const selectedAmt = parseNumber(e.currentTarget.value);
        setSelectedAmt(e.currentTarget.value);
        setModel((prev) => prev && { ...prev, selectedAmt });
    }, []);

    const handleCaptchaChange = useCallback((customerInput: string) => {
        setModel((prev) => prev && { ...prev, captcha: { customerInput } });
    }, []);

    const variables = useMemo(() => ({ SelectedAmt: selectedAmt }), [selectedAmt]);

    const handleContinueClick = useCallback(
        async (e: MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();

            if (!model) return;
            setShowError(false);
            const valid = !model.disclosures?.length || model.disclosures.every((x) => x.agree || !x.required);
            if (!valid) {
                setShowError(true);
                window.scrollTo(0, 0);
                return;
            }

            setBusy(true);
            const result = await client.getCash(model, key);
            setBusy(false);
            const modelState = getModelState(result.errors);
            if (modelState) setModelState(modelState);

            if (result.data?.getCash?.redirect) {
                nav.redirect(result.data.getCash.redirect);
            } else {
                setModel(prev => prev ? { ...prev, ...result.data?.getCash } : prev);
            }
        },
        [model, client, key, nav]
    );

    const disabled = busy || !model;

    return (
        <form className="container">
            <h1>Cash advance</h1>
            {model && <div className="mb-3 text-center">Your available credit is {formatCurrency(model.maxAmt)}.</div>}

            <div className="form-group">
                <label htmlFor="SelectedAmt" className="form-label">
                    How much cash do you need?
                </label>
                <div className="input-group">
                    <CurrencyTextbox
                        id="CustomPaymentAmount"
                        disabled={disabled}
                        className="input-lg"
                        onChange={handleSelectedAmtChange}
                        value={selectedAmt}
                        placeholder="0.00"
                    />
                </div>
                <p className="help-block small mb-0">(Minimum {formatCurrency(model?.minAmt, 0)})</p>
                <ValidationMessage name="SelectedAmt" modelState={modelState} />
            </div>

            <div className="form-group">Please verify your bank account to ensure your cash advance is processed correctly.</div>

            {model?.bankAccount &&
                (model.bankAccount.bankInstitutionNumber ? (
                    <div className="form-group row">
                        <div className="col-6">
                            <label className="form-label text-right" htmlFor="BankAccount.BankTransitNumber">
                                Branch Transit Number
                            </label>
                            <Textbox
                                type="text"
                                disabled
                                id="BankAccount.BankTransitNumber"
                                value={model?.bankAccount?.bankTransitNumber}
                            />
                        </div>
                        <div className="col-6">
                            <label className="form-label text-right" htmlFor="BankAccount.BankInstitutionNumber">
                                Financial Institution Number
                            </label>
                            <Textbox
                                type="text"
                                disabled
                                id="BankAccount.BankInstitutionNumber"
                                value={model?.bankAccount?.bankInstitutionNumber}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="form-group">
                        <label className="form-label text-right" htmlFor="BankAccount.BankTransitNumber">
                            Your routing/ABA number
                        </label>
                        <Textbox type="text" disabled id="BankAccount.BankTransitNumber" value={model?.bankAccount?.bankTransitNumber} />
                    </div>
                ))}

            <div className="form-group">
                <label className="form-label text-right pt-2" htmlFor="BankAccount.BankAccountNumber">
                    Your account number
                </label>
                <Textbox type="text" disabled id="BankAccount.BankAccountNumber" value={model?.bankAccount?.bankAccountNumber} />
            </div>

            <div className="form-group">
                <p>
                    Your bank has been identified as <strong>{model?.bankAccount?.bankName}</strong>.
                </p>
                <p>
                    If this is incorrect please contact Customer Service at <CustomerServicePhoneLink /> to update your account.
                </p>
            </div>

            {model && (
                <RefinanceDisclosures
                    model={model}
                    onChange={setModel}
                    modelState={modelState}
                    showError={showError}
                    variables={variables}
                    disabled={disabled}
                />
            )}

            <p className="mt-6">For security reasons please enter the verification code as shown below.</p>
            <Captcha modelState={modelState} onChange={handleCaptchaChange} value={model?.captcha?.customerInput} />
            <div className="text-center">
                <button className="btn btn-primary max-w-full mb-3 mt-6 md:max-w-xs" onClick={handleContinueClick} disabled={disabled}>
                    {busy && <Spinner inline />}
                    Continue
                </button>
                <Link className="btn btn-link" to={Routes.User.AccountSummary}>
                    Cancel
                </Link>
            </div>
        </form>
    );
}
